import { UnrecoverableError } from './UnrecoverableError'

import { CMS_ERROR_CODES_MAPPING } from '@/constants/errors/code'
import { ErrorPrefix } from '@/constants/errors/prefix'

type CMSError = {
  error_code?: string
  error_message?: string
  status?: number
  statusText?: string
}

/**
 * This class represents unrecoverable errors related to CMS operations.
 *
 * It provides specific details to build a CMS error
 * code to the {@link UnrecoverableError} class.
 *
 * @see ERRORS.md and ERROR_CODES.md for more details.
 */
export class CMSUnrecoverableError extends UnrecoverableError {
  readonly prefix = ErrorPrefix.CMS

  /**
   * @param errorPayload error payload returned from the CMS
   */
  constructor(errorPayload: unknown) {
    super(errorPayload)
  }

  /**
   * Returns the error payload as a CMSError if it matches the structure.
   * An error payload can be anything, so we need to check if it has the properties we expect.
   */
  private getErrorPayloadAsCMSError(): undefined | CMSError {
    if (
      this.errorPayload &&
      typeof this.errorPayload === 'object' &&
      'error_code' in this.errorPayload
    )
      return this.errorPayload as CMSError
  }

  private getErrorPayloadMessage() {
    const cmsError = this.getErrorPayloadAsCMSError()
    if (cmsError) return cmsError.error_message
  }

  /**
   * Returns the code part which matches the patterns defined in {@link CMS_ERROR_CODES_MAPPING}
   */
  protected getCode() {
    const cmsError = this.getErrorPayloadMessage()
    if (!cmsError) return
    return CMS_ERROR_CODES_MAPPING.find(({ regex }) => regex.test(cmsError))
      ?.code
  }

  /**
   * Returns the status code from the CMS error payload if available.
   * CMS Errors does not have status code as a constant property in their payload as some
   * errors are encapsulated into non-HTTP error responses, such as empty array responses with 200 status,
   * which returns only an error payload with a message but no status code.
   */
  protected getStatusCode() {
    const cmsError = this.getErrorPayloadAsCMSError()
    if (cmsError && 'status' in cmsError && typeof cmsError.status === 'number')
      return cmsError.status
  }
}
